import type { WsMessage } from '@shared/api/ws/types'
import type { Emitter } from 'nanoevents'
import type { LayoutSnapshot } from '@/proxy/shared/layout'
import { IFRAME_MESSAGE_TYPE, IFrameMessageType, sendIframeMessage } from '@shared/iframe-messages'
import { topicMatches } from '@shared/util/topic-matcher'
import { createNanoEvents } from 'nanoevents'
import { getLayoutCache, setLayoutCache } from '@/proxy/shared/layout-cache'

type KnownTopics
  = | '@history'
    | '@visibility'
    | '@message'
    | '@state-error'
    | '@layout-change'
    | '@layout-breakpoint'

export type Events = {
  /** Emitted when pages are updated */
  '@history': (data: { path: string, navId?: number }) => void
  '@visibility': (visible: boolean) => void
  '@message': (data: WsMessage) => void
  '@state-error': (data: { error: string, key?: string }) => void
  '@layout-change': (snapshot: LayoutSnapshot) => void
  '@layout-breakpoint': (data: { name: string, width: number }) => void
} & {
  [K in string as K extends KnownTopics ? never : K]: (data: WsMessage) => void;
}
export function createProxyEvents(): Emitter<Events> {
  return createNanoEvents<Events>()
}

export function subscribeToEvents(emitter: Emitter<Events>) {
  window.addEventListener('message', (event) => {
    // The proxy listener runs for every postMessage delivered to this
    // window — browser extensions, sibling iframes, and userscripts can
    // all send arbitrary non-JSON data. Guard the parse so a malformed
    // message never crashes the listener (which would silently kill all
    // downstream layout / state / history events).
    let dataNormalized: any
    try {
      dataNormalized = typeof event.data === 'string' ? JSON.parse(event.data) : event.data
    }
    catch {
      return
    }
    if (!dataNormalized || typeof dataNormalized !== 'object')
      return
    if (dataNormalized.type === IFRAME_MESSAGE_TYPE && dataNormalized.action === IFrameMessageType.UrlWasUpdatedInParent) {
      emitter.emit('@history', dataNormalized)
    }
    if (dataNormalized.type === IFRAME_MESSAGE_TYPE && dataNormalized.action === IFrameMessageType.VisibilityWasUpdatedInParent) {
      emitter.emit('@visibility', dataNormalized.visible)
    }

    if (dataNormalized.type === IFRAME_MESSAGE_TYPE && dataNormalized.action === IFrameMessageType.OnSubscription) {
      emitter.emit('@message', dataNormalized.message)
    }
    if (dataNormalized.type === IFRAME_MESSAGE_TYPE && dataNormalized.action === IFrameMessageType.OnStateError) {
      emitter.emit('@state-error', dataNormalized)
    }
    if (dataNormalized.type === IFRAME_MESSAGE_TYPE && dataNormalized.action === IFrameMessageType.OnLayoutChange) {
      // Update the cache FIRST so `host.layout.snapshot` reads inside an
      // `@layout-change` handler see the new value (contract used by
      // useWippyLayout). Single-writer design — no separate listener
      // inside layout.ts.
      setLayoutCache((dataNormalized.snapshot as LayoutSnapshot | null) ?? null)
      emitter.emit('@layout-change', dataNormalized.snapshot)
    }
    if (dataNormalized.type === IFRAME_MESSAGE_TYPE && dataNormalized.action === IFrameMessageType.OnLayoutPanelChanged) {
      // Per-panel delta — update only the affected panel in the cached
      // snapshot in place, then fire `@layout-change` with a fresh
      // snapshot reference. Existing subscribers (`useWippyLayout`,
      // custom `host.on('@layout-change', ...)` consumers) keep working
      // unchanged; they just see the merged result instead of paying
      // the cost of a full-snapshot parse for one panel's state change.
      const cur = getLayoutCache()
      const panelId = dataNormalized.panelId as string | undefined
      const panelDef = dataNormalized.panelDef as Record<string, unknown> | undefined
      if (typeof panelId === 'string' && panelDef && cur && cur.panels) {
        const nextPanels = { ...(cur.panels as Record<string, unknown>), [panelId]: panelDef }
        const next: LayoutSnapshot = { ...cur, panels: nextPanels }
        setLayoutCache(next)
        emitter.emit('@layout-change', next)
      }
      else if (typeof panelId === 'string' && panelDef && (!cur || !cur.panels)) {
        // Race: per-panel delta arrived before the first full
        // OnLayoutChange snapshot. Drop the update (we have no baseline
        // to merge into) but make the dropped path observable.
        // eslint-disable-next-line no-console
        console.debug('[wippy-proxy] OnLayoutPanelChanged dropped — no baseline snapshot yet', { panelId })
      }
    }
    if (dataNormalized.type === IFRAME_MESSAGE_TYPE && dataNormalized.action === IFrameMessageType.OnLayoutBreakpoint) {
      emitter.emit('@layout-breakpoint', { name: dataNormalized.name, width: dataNormalized.width })
    }
    // OnLayoutBroadcast is intentionally NOT routed through the generic
    // event bus — layout-bus channels are owned by `host.layout.on(...)`,
    // separate from WS topics. See src/proxy/shared/layout.ts.
  })
}

export function createEvents() {
  const emitter = createProxyEvents()
  subscribeToEvents(emitter)

  // Track which topics have been subscribed at the host level to avoid
  // sending duplicate CmdSubscribe messages. Ref-counted so CmdUnSubscribe
  // is only sent when the last listener for a topic unsubscribes.
  const subscribedTopics = new Map<string, number>()

  function hostSubscribe(topic: string) {
    const count = subscribedTopics.get(topic) || 0
    subscribedTopics.set(topic, count + 1)
    if (count === 0)
      sendIframeMessage(IFrameMessageType.CmdSubscribe, { topic })
  }

  function hostUnsubscribe(topic: string) {
    const count = subscribedTopics.get(topic) || 0
    if (count <= 1) {
      subscribedTopics.delete(topic)
      sendIframeMessage(IFrameMessageType.CmdUnSubscribe, { topic })
    }
    else {
      subscribedTopics.set(topic, count - 1)
    }
  }

  // Auto-unsubscribe all topics on iframe unload
  window.addEventListener('unload', () => {
    for (const topic of subscribedTopics.keys())
      sendIframeMessage(IFrameMessageType.CmdUnSubscribe, { topic })
    subscribedTopics.clear()
  })

  const on = <T extends string>(topicPattern: T, callback:
  T extends KnownTopics
    ? Events[T] : Events['@message']) => {
    if (topicPattern === '@history') {
      return emitter.on('@history', callback as Events['@history'])
    }
    if (topicPattern === '@visibility') {
      return emitter.on('@visibility', callback as Events['@visibility'])
    }
    if (topicPattern === '@state-error') {
      return emitter.on('@state-error', callback as Events['@state-error'])
    }
    if (topicPattern === '@layout-change') {
      return emitter.on('@layout-change', callback as Events['@layout-change'])
    }
    if (topicPattern === '@layout-breakpoint') {
      return emitter.on('@layout-breakpoint', callback as Events['@layout-breakpoint'])
    }
    if (topicPattern === '@message') {
      hostSubscribe('*')
      hostSubscribe('*:*')
      hostSubscribe('*:*:*:*')
      const unsub = emitter.on('@message', callback as Events['@message'])
      return () => {
        unsub()
        hostUnsubscribe('*')
        hostUnsubscribe('*:*')
        hostUnsubscribe('*:*:*:*')
      }
    }

    hostSubscribe(topicPattern)
    const unsub = emitter.on('@message', (message) => {
      if (topicMatches(message.topic, topicPattern)) {
        (callback as Events['@message'])(message)
      }
    })
    return () => {
      unsub()
      hostUnsubscribe(topicPattern)
    }
  }
  return on
}
